// pages/monthly-tests/MonthlyTestList.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Tooltip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterIcon,
  PictureAsPdf as PdfIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Publish as PublishIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
// import AuthService from '../../Auth/AuthService';
import { testsApi } from './api/apiClientMonthly';

const MonthlyTestList = ({ user }) => {
  const navigate = useNavigate();

  // ---------- State ----------
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [filterStatus, setFilterStatus] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterAcademicYear, setFilterAcademicYear] = useState('');

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [stagedFilters, setStagedFilters] = useState([]);

  // Ref to abort in-flight requests / timeouts
  const abortRef = useRef(null);
  const hasFetched = useRef(false);

  // ---------- Debounce search ----------
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // reset to first page on new search
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ---------- Fetch tests ----------
  const fetchTests = useCallback(async () => {
    // Abort previous request / clear pending timer
    if (abortRef.current) {
      abortRef.current.abort?.();
      clearTimeout(abortRef.current.timeoutId);
    }

    setLoading(true);
    setError('');

    try {

      const response = await testsApi.getAll({
        page,
        size: rowsPerPage,
        search: debouncedSearch || undefined,
        status: filterStatus || undefined,
        classId: filterClass || undefined,
        academicYear: filterAcademicYear || undefined,
        sortBy: 'testDate',
        direction: 'DESC'
      }
      );

      // Normalize response — supports both plain arrays and paged responses
      const rawData = response?.data ?? response;
      let content = [];
      let total = 0;

      if (Array.isArray(rawData)) {
        // Plain array response
        content = rawData;
        total = rawData.length;
      } else if (rawData && Array.isArray(rawData.content)) {
        // PagedResponse<T> { content, totalElements, ... }
        content = rawData.content;
        total = rawData.totalElements ?? rawData.content.length;
      } else if (rawData && Array.isArray(rawData.data)) {
        // Wrapped { data: [...] }
        content = rawData.data;
        total = rawData.totalElements ?? rawData.data.length;
      }

      setTests(content);
      setTotalCount(total);
      setStagedFilters(content);
    } catch (err) {
      console.error('Error fetching tests:', err);
      setError(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load tests. Please try again.'
      );
      setTests([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    debouncedSearch,
    filterStatus,
    filterClass,
    filterAcademicYear
  ]);

  // ---------- Trigger on filter/page changes ----------
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchTests();
    return () => {
      if (abortRef.current) {
        abortRef.current.abort?.();
        clearTimeout(abortRef.current.timeoutId);
      }
    };
  }, [fetchTests]);

  // ---------- Handlers ----------
  const handleMenuClick = (event, test) => {
    setAnchorEl(event.currentTarget);
    setSelectedTest(test);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTest(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTest) return;
    try {
      await testsApi.delete(selectedTest.id);
      // Optimistic update + refetch to keep pagination correct
      setTests((prev) => prev.filter((t) => t.id !== selectedTest.id));
      setTotalCount((prev) => Math.max(0, prev - 1));
      setDeleteDialogOpen(false);
      setSnackbar({ open: true, message: 'Test deleted successfully', severity: 'success' });

      // Refresh list from server
      fetchTests();
    } catch (err) {
      console.error('Error deleting test:', err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Failed to delete test',
        severity: 'error'
      });
      setDeleteDialogOpen(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      DRAFT: { color: 'default', icon: <ScheduleIcon /> },
      OPEN: { color: 'warning', icon: <AssessmentIcon /> },
      COMPLETED: { color: 'info', icon: <CheckCircleIcon /> },
      PUBLISHED: { color: 'success', icon: <PublishIcon /> }
    };
    const config = statusConfig[status] || statusConfig.DRAFT;
    return (
      <Chip label={status} color={config.color} size="small" icon={config.icon} />
    );
  };

  // const getProgressPercentage = (entered, total) => {
  //   if (!total || total === 0) return 0;
  //   return Math.round((entered / total) * 100);
  // };

  const handleClearFilters = () => {
    setFilterStatus('');
    setFilterClass('');
    setFilterAcademicYear('');
    setPage(0);
  };


  const handleSearchTerm = (e) => {
    setTests(stagedFilters);
    const search = e.target.value;
    setSearchTerm(search);
    setPage(0);                                   // reset to first page

    const term = search.trim().toLowerCase();
    if (!term) return tests;
    const demo = stagedFilters.filter((t) =>
      (t.testName ?? '').toLowerCase().includes(term) ||
      (t.status ?? '').toLowerCase().includes(term) ||
      (t.subject ?? '').toLowerCase().includes(term) ||
      (t.standard ?? '').toLowerCase().includes(term) ||
      (t.academicYear ?? '').toLowerCase().includes(term)
    );
    setTests(demo);
  };

  const handleRefresh = () => {
    fetchTests();
  };

  // ---------- Render ----------
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Typography variant="h4" component="h1">
          Monthly Tests
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          {user?.roles === "ADMIN" && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/tests/create')}
            >
              Create Test
            </Button>

          )}
        </Box>
      </Box>

      {/* Error banner */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Tests
              </Typography>
              <Typography variant="h5">{totalCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Published
              </Typography>
              <Typography variant="h5" color="success.main">
                {tests.filter((t) => t.status === 'PUBLISHED').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                In Progress
              </Typography>
              <Typography variant="h5" color="warning.main">
                {tests.filter((t) => t.status === 'OPEN').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Drafts
              </Typography>
              <Typography variant="h5" color="text.secondary">
                {tests.filter((t) => t.status === 'DRAFT').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4} size={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Class, TestName, Subject, Status, academicYear"
              value={searchTerm}
              onChange={handleSearchTerm}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
                flexWrap: 'wrap'
              }}
            >
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setFilterDialogOpen(true)}
              >
                Filters
              </Button>
              {(filterStatus || filterClass || filterAcademicYear) && (
                <Button variant="text" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Test Name</TableCell>
              <TableCell>Class &amp; Section</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Test Date</TableCell>
              <TableCell align="center">Max Marks</TableCell>
              <TableCell align="center">Status</TableCell>
              {/* <TableCell align="center">Progress</TableCell> */}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : tests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">No tests found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              tests.map((test) => (
                <TableRow key={test.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {test.testName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {test.academicYear}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {test.standard} - {test.section}
                  </TableCell>
                  <TableCell>{test.subject}</TableCell>
                  <TableCell>
                    {test.testDate
                      ? format(new Date(test.testDate), 'dd MMM yyyy')
                      : '-'}
                  </TableCell>
                  <TableCell align="center">{test.maxMarks}</TableCell>
                  <TableCell align="center">{getStatusChip(test.status)}</TableCell>
                  {/* <TableCell align="center">
                    <Tooltip
                      title={`${test.marksEntered ?? 0}/${test.totalStudents ?? 0} students`}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {getProgressPercentage(
                            test.marksEntered,
                            test.totalStudents
                          )}
                          %
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell> */}
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, test)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            navigate(`/tests/${selectedTest?.id}`);
            handleMenuClose();
          }}
        >
          <ViewIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate(`/tests/${selectedTest?.id}/edit`);
            handleMenuClose();
          }}
        >
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate(`/tests/${selectedTest?.id}/marks/${selectedTest?.standard}`);
            handleMenuClose();
          }}
        >
          <AssessmentIcon sx={{ mr: 1 }} fontSize="small" />
          Enter Marks
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate(`/tests/${selectedTest?.id}/files`);
            handleMenuClose();
          }}
        >
          <PdfIcon sx={{ mr: 1 }} fontSize="small" />
          Manage Files
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{selectedTest?.testName}&quot;?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Filter Tests</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} size={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="PUBLISHED">Published</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} size={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Academic Year</InputLabel>
                <Select
                  value={filterAcademicYear}
                  onChange={(e) => setFilterAcademicYear(e.target.value)}
                  label="Academic Year"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="2024-2025">2024-2025</MenuItem>
                  <MenuItem value="2023-2024">2023-2024</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} size={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Class</InputLabel>
                <Select
                  value={filterClass}
                  onChange={(e) => setFilterClass(e.target.value)}
                  label="Class"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="1">Class 10</MenuItem>
                  <MenuItem value="2">Class 11</MenuItem>
                  <MenuItem value="3">Class 12</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setFilterDialogOpen(false);
              setPage(0);
              // fetchTests will run automatically via useEffect deps
            }}
            variant="contained"
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MonthlyTestList;